import { useMemo, useState } from 'react'
import styled from 'styled-components'
import { Colors, Spacing, Typography } from '../../styles'
import { checkboxSize } from '../../styles/dimensions'
import { isTaskOverviewView } from '../../utils/overview'
import { TTaskV4 } from '../../utils/types'
import GTShadowContainer from '../atoms/GTShadowContainer'
import MarkTaskDoneButton from '../atoms/buttons/MarkTaskDoneButton'
import NoStyleButton from '../atoms/buttons/NoStyleButton'
import useOverviewLists from '../overview/useOverviewLists'

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    ${Typography.body.large};
    color: ${Colors.text.muted};
    margin-bottom: ${Spacing._16};
`
const CardHeader = styled.div`
    display: flex;
    gap: ${Spacing._16};
    align-items: center;
    margin-bottom: ${Spacing._24};
`
const CardBody = styled.div`
    white-space: pre-wrap;
    margin-left: calc(${checkboxSize.parentContainer} + ${Spacing._16});
`
const SwitchText = styled(NoStyleButton)`
    line-height: inherit;
    color: ${Colors.text.ink};
    cursor: pointer;
    user-select: none;
    ${Typography.bold};
`
const mod = (n: number, m: number) => {
    return ((n % m) + m) % m
}
interface CardSwitcherProps {
    viewId: string
    // Rendered when the list cannot be resolved — an event can outlive the list
    // it was scheduled from, and showing nothing would leave a blank panel.
    fallback?: React.ReactNode
}

const CardSwitcher = ({ viewId, fallback = null }: CardSwitcherProps) => {
    const { lists } = useOverviewLists()
    const list = useMemo(() => lists?.find((view) => view.id === viewId && isTaskOverviewView(view)), [lists, viewId])

    const [cardIndex, setCardIndex] = useState(0)
    const card = useMemo(() => list?.view_items[cardIndex] as TTaskV4 | undefined, [cardIndex, viewId, list])

    const onClickPrevious = () => {
        if (list == null) return
        const index = mod(cardIndex - 1, list.view_item_ids.length)
        if (index < 0) setCardIndex(list.view_item_ids.length - 1)
        else setCardIndex(index)
    }
    const onClickNext = () => {
        if (list == null) return
        const index = mod(cardIndex + 1, list.view_item_ids.length)
        if (index >= list.view_item_ids.length) setCardIndex(0)
        else setCardIndex(index)
    }

    if (!list) return <>{fallback}</>
    if (list.view_item_ids.length === 0 || !card) return null
    return (
        <div>
            <Header>
                <SwitchText type="button" onClick={onClickPrevious}>
                    Previous
                </SwitchText>
                <div>
                    Task {cardIndex + 1} of {list.view_item_ids.length}
                </div>
                <SwitchText type="button" onClick={onClickNext}>
                    Next
                </SwitchText>
            </Header>
            <GTShadowContainer>
                <CardHeader>
                    <MarkTaskDoneButton isDone={card.is_done} taskId={card.id} isSelected={false} />
                    {card.title}
                </CardHeader>
                <CardBody>{card.body}</CardBody>
            </GTShadowContainer>
        </div>
    )
}

export default CardSwitcher
